/**
 * Concierge — Reassignments (PROD-7320, PROD-8493). Super-user tool to bulk-reassign post-trial
 * bucket students off a source concierge to a target concierge. PROD-8493 dropped the hardcoded
 * "never finished a session" pool in favor of a User Type filter — Session Booked/Scheduled,
 * No-Show, Not Booked — and no longer auto-selects students in the preview. Flow: pick source →
 * pod → user type → date range → view eligible (popup) → pick target + N → preview (random N) →
 * review/select (popup) → commit. Reached from /concierge-pods (already SUPER_USER-only); the
 * goserver endpoints enforce SUPER_USER too.
 */

import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import moment from 'moment-timezone';
import CommonHeader from '../../util_components/common_header';
import CustomLoading from '../../util_components/custom_loading';
import CustomModal from '../../util_components/custom_modal';
import Button from '../../util_components/button';
import { get_api_url } from '../../../utils/urls';
import { get_api, post_api } from '../../../redux/api_funcs';
import * as actions from '../../../redux/action_creators';

const BASE = '/v2/cms_user/concierge_pods';
const PAGE_STYLE = {
  background: 'var(--surface-page)',
  color: 'var(--text-primary)',
  minHeight: '100%'
};
const CARD_STYLE = { background: 'var(--surface-card)', borderColor: 'var(--border-default)' };
const MUTED_STYLE = { color: 'var(--text-muted)' };
const INPUT_STYLE = {
  background: 'var(--input-bg)',
  borderColor: 'var(--input-border)',
  color: 'var(--input-text)'
};
const WARN_STYLE = {
  background: 'var(--state-warning-bg)',
  color: 'var(--state-warning-text)',
  borderColor: 'var(--state-warning)'
};
const MODAL_INNER = {
  background: 'var(--surface-card)',
  color: 'var(--text-primary)',
  maxWidth: '900px',
  // the .custom-modal-inner-container-close class (used with showCloseBtn) has no overflow, so
  // set it here — the ENTIRE popup scrolls within its max-height:90%. This keeps Cancel/Confirm
  // reachable on short/low-height screens (Windows): the user scrolls the whole popup to them,
  // rather than the table scrolling internally (which could still hide the footer).
  overflow: 'auto'
};
const LINK_STYLE = { color: 'var(--brand-primary)' };

const BUCKET_LABELS = {
  POST_TRIAL_NOT_RESET: 'Not reset',
  POST_TRIAL_RESET: 'Reset',
  POST_TRIAL_BOOKED: 'Booked'
};

// PROD-8493 user-type filter. Values mirror the goserver constants (SCHEDULED/NO_SHOW/
// NOT_BOOKED); '' = all. The ticket's "Members" category is intentionally omitted.
const USER_TYPE_LABELS = {
  SCHEDULED: 'Session Booked / Scheduled',
  NO_SHOW: 'No-Show',
  NOT_BOOKED: 'Not Booked'
};
const USER_TYPE_OPTIONS = [
  { value: '', label: 'All types' },
  { value: 'SCHEDULED', label: USER_TYPE_LABELS.SCHEDULED },
  { value: 'NO_SHOW', label: USER_TYPE_LABELS.NO_SHOW },
  { value: 'NOT_BOOKED', label: USER_TYPE_LABELS.NOT_BOOKED }
];

const advisor_name = (a) => `${a.first_name || ''} ${a.last_name || ''}`.trim() || a.uuid;
const ist_date = (d) => (d ? moment(d).tz('Asia/Kolkata').format('MMM D, YYYY') : '—');

class ConciergePodsReassignments extends Component {
  state = {
    advisors: [],
    source_uuid: '',
    region_id: '',
    user_type: '',
    from: '',
    to: '',
    pods: [],
    total_eligible: 0,
    pods_loading: false,
    show_eligible: false,
    eligible_loading: false,
    eligible_list: [],
    target_uuid: '',
    count: '',
    preview_loading: false,
    show_review: false,
    review_students: [],
    review_selected: {},
    warnings: { not_in_pod: false, not_approved: false },
    preview_available: 0,
    committing: false,
    result: null
  };

  componentDidMount() {
    // default the bucket-entry window to the last 30 days (preserves prior behavior)
    const to = moment().format('YYYY-MM-DD');
    const from = moment().subtract(30, 'days').format('YYYY-MM-DD');
    this.setState({ from, to }, this.load_advisors);
  }

  err = (e, fallback) => {
    const { set_notification_variable } = this.props;
    let msg = fallback;
    if (e && e.response && e.response.data) msg = e.response.data.reason || fallback;
    set_notification_variable(true, 'error', msg);
  };

  load_advisors = () => {
    get_api(get_api_url(`${BASE}/concierge_teachers`), true)
      .then((res) => this.setState({ advisors: (res.data && res.data.data) || [] }))
      .catch((e) => this.err(e, 'Failed to load the concierge list'));
  };

  on_source_change = (e) => {
    const source_uuid = e.target.value;
    this.setState(
      {
        source_uuid,
        region_id: '',
        pods: [],
        total_eligible: 0,
        // reset the whole downstream wizard so no stale target/preview/review carries over
        target_uuid: '',
        count: '',
        show_eligible: false,
        eligible_list: [],
        show_review: false,
        review_students: [],
        review_selected: {},
        warnings: { not_in_pod: false, not_approved: false },
        preview_available: 0,
        result: null
      },
      () => {
        if (source_uuid) this.load_pods();
      }
    );
  };

  load_pods = () => {
    const { source_uuid, from, to, user_type } = this.state;
    this.setState({ pods_loading: true });
    get_api(
      get_api_url(
        `${BASE}/reassign/pods?teacher_uuid=${encodeURIComponent(source_uuid)}` +
          `&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}` +
          `&user_type=${encodeURIComponent(user_type)}`
      ),
      true
    )
      .then((res) => {
        const d = (res.data && res.data.data) || {};
        this.setState({
          pods: d.pods || [],
          total_eligible: d.total || 0,
          pods_loading: false
        });
      })
      .catch((e) => {
        this.setState({ pods_loading: false });
        this.err(e, 'Failed to load eligible pods');
      });
  };

  on_change = (e) => this.setState({ [e.target.name]: e.target.value });

  // Changing the pod filter invalidates any in-flight preview/review built for another pod.
  on_region_change = (e) =>
    this.setState({
      region_id: e.target.value,
      show_review: false,
      review_students: [],
      review_selected: {},
      warnings: { not_in_pod: false, not_approved: false },
      preview_available: 0,
      result: null
    });

  // Changing the date window changes the eligible pool → reset preview/review and reload counts.
  on_window_change = (e) => {
    const { source_uuid } = this.state;
    this.setState(
      {
        [e.target.name]: e.target.value,
        show_review: false,
        review_students: [],
        review_selected: {},
        warnings: { not_in_pod: false, not_approved: false },
        preview_available: 0,
        result: null
      },
      () => {
        if (source_uuid) this.load_pods();
      }
    );
  };

  // Changing the user-type filter changes the eligible pool + per-pod counts → clear the stale
  // total/pod counts and any open eligible popup, reset in-flight preview/review, then reload
  // the (type-filtered) pod counts (PROD-8493).
  on_user_type_change = (e) => {
    const { source_uuid } = this.state;
    this.setState(
      {
        user_type: e.target.value,
        total_eligible: 0,
        pods: [],
        show_eligible: false,
        eligible_list: [],
        show_review: false,
        review_students: [],
        review_selected: {},
        warnings: { not_in_pod: false, not_approved: false },
        preview_available: 0,
        result: null
      },
      () => {
        if (source_uuid) this.load_pods();
      }
    );
  };

  eligible_count = () => {
    const { region_id, pods, total_eligible } = this.state;
    if (!region_id) return total_eligible;
    const p = pods.find((x) => String(x.region_id) === String(region_id));
    return p ? p.eligible_count : 0;
  };

  open_eligible = () => {
    this.setState({ show_eligible: true, eligible_loading: true, eligible_list: [] });
    const { source_uuid, region_id, from, to, user_type } = this.state;
    let url =
      `${BASE}/reassign/eligible?teacher_uuid=${encodeURIComponent(source_uuid)}` +
      `&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}` +
      `&user_type=${encodeURIComponent(user_type)}`;
    if (region_id) url += `&region_id=${encodeURIComponent(region_id)}`;
    get_api(get_api_url(url), true)
      .then((res) =>
        this.setState({
          eligible_list: (res.data && res.data.data && res.data.data.students) || [],
          eligible_loading: false
        })
      )
      .catch((e) => {
        this.setState({ eligible_loading: false });
        this.err(e, 'Failed to load eligible students');
      });
  };

  close_eligible = () => this.setState({ show_eligible: false });

  do_preview = () => {
    const { source_uuid, target_uuid, region_id, count, from, to, user_type } = this.state;
    const { set_notification_variable } = this.props;
    const n = parseInt(count, 10);
    if (!source_uuid) {
      set_notification_variable(true, 'error', 'Pick a source concierge');
      return;
    }
    if (!target_uuid) {
      set_notification_variable(true, 'error', 'Pick a target concierge');
      return;
    }
    if (source_uuid === target_uuid) {
      set_notification_variable(true, 'error', 'Source and target must differ');
      return;
    }
    if (!n || n <= 0) {
      set_notification_variable(true, 'error', 'Enter a number greater than 0');
      return;
    }
    this.setState({ preview_loading: true, result: null });
    const body = {
      source_teacher_uuid: source_uuid,
      target_teacher_uuid: target_uuid,
      region_id: region_id ? parseInt(region_id, 10) : 0,
      count: n,
      from,
      to,
      user_type
    };
    post_api(get_api_url(`${BASE}/reassign/preview`), body, true)
      .then((res) => {
        const d = (res.data && res.data.data) || {};
        const students = d.students || [];
        // PROD-8493: do NOT auto-select — the operator opts each student in explicitly.
        this.setState({
          preview_loading: false,
          show_review: true,
          review_students: students,
          review_selected: {},
          warnings: d.warnings || { not_in_pod: false, not_approved: false },
          preview_available: d.available || 0
        });
      })
      .catch((e) => {
        this.setState({ preview_loading: false });
        this.err(e, 'Failed to build the preview');
      });
  };

  toggle_select = (uuid) =>
    this.setState((st) => ({
      review_selected: { ...st.review_selected, [uuid]: !st.review_selected[uuid] }
    }));

  selected_uuids = () => {
    const { review_selected } = this.state;
    return Object.keys(review_selected).filter((u) => review_selected[u]);
  };

  close_review = () => this.setState({ show_review: false });

  do_commit = () => {
    const { source_uuid, target_uuid, region_id } = this.state;
    const { set_notification_variable } = this.props;
    const uuids = this.selected_uuids();
    if (uuids.length === 0) {
      set_notification_variable(true, 'error', 'Select at least one student');
      return;
    }
    this.setState({ committing: true });
    const body = {
      source_teacher_uuid: source_uuid,
      target_teacher_uuid: target_uuid,
      region_id: region_id ? parseInt(region_id, 10) : 0,
      student_uuids: uuids
    };
    post_api(get_api_url(`${BASE}/reassign/commit`), body, true)
      .then((res) => {
        const d = (res.data && res.data.data) || {};
        const moved = d.moved || 0;
        const skipped = d.skipped || 0;
        const notFound = d.not_found || 0;
        // Never present a partial/empty commit as a clean success.
        const parts = [`Reassigned ${moved}`];
        if (skipped) parts.push(`skipped ${skipped} (no longer on source)`);
        if (notFound) parts.push(`not found ${notFound}`);
        const type = moved === 0 || skipped || notFound ? 'warning' : 'success';
        set_notification_variable(true, type, parts.join(', '));
        this.setState({ committing: false, show_review: false, result: d, count: '' });
        this.load_pods(); // refresh eligible counts after the move
      })
      .catch((e) => {
        this.setState({ committing: false });
        this.err(e, 'Reassignment failed');
      });
  };

  render_student_row = (s, withCheckbox) => {
    const { review_selected } = this.state;
    return (
      <tr key={s.uuid} className="border-b" style={{ borderColor: 'var(--border-default)' }}>
        {withCheckbox && (
          <td className="py-1 pr-2">
            <input
              type="checkbox"
              aria-label={`Include ${s.email || s.uuid}`}
              checked={!!review_selected[s.uuid]}
              onChange={() => this.toggle_select(s.uuid)}
            />
          </td>
        )}
        <td className="py-1 pr-3">
          <a
            href={`/studentprofile?id=${encodeURIComponent(s.uuid)}`}
            target="_blank"
            rel="noopener noreferrer"
            style={LINK_STYLE}
          >
            {`${s.first_name || ''} ${s.last_name || ''}`.trim() || s.email || s.uuid}
          </a>
        </td>
        <td className="py-1 pr-3" style={MUTED_STYLE}>
          {s.email || '—'}
        </td>
        <td className="py-1 pr-3" style={MUTED_STYLE}>
          {s.iana_timezone || '—'}
        </td>
        <td className="py-1 pr-3" style={MUTED_STYLE}>
          {s.pod_name || '—'}
        </td>
        <td className="py-1 pr-3" style={MUTED_STYLE}>
          {USER_TYPE_LABELS[s.user_type] || s.user_type || '—'}
        </td>
        <td className="py-1 pr-3" style={MUTED_STYLE}>
          {BUCKET_LABELS[s.current_bucket] || s.current_bucket}
        </td>
        <td className="whitespace-nowrap py-1 pr-3" style={MUTED_STYLE}>
          {ist_date(s.entered_bucket_at)}
        </td>
        <td className="whitespace-nowrap py-1" style={MUTED_STYLE}>
          {ist_date(s.last_finished_at)}
        </td>
      </tr>
    );
  };

  render_students_table = (students, withCheckbox) => (
    <div className="mt-2 overflow-x-auto">
      <table className="report-cells-left w-full text-left text-xs">
        <thead>
          <tr className="border-b" style={{ ...MUTED_STYLE, borderColor: 'var(--border-default)' }}>
            {withCheckbox && <th className="py-1 pr-2 text-left font-medium" />}
            <th className="py-1 pr-3 text-left font-medium">Name</th>
            <th className="py-1 pr-3 text-left font-medium">Email</th>
            <th className="py-1 pr-3 text-left font-medium">Timezone</th>
            <th className="py-1 pr-3 text-left font-medium">Pod</th>
            <th className="py-1 pr-3 text-left font-medium">Type</th>
            <th className="py-1 pr-3 text-left font-medium">Bucket</th>
            <th className="py-1 pr-3 text-left font-medium">Entered (IST)</th>
            <th className="py-1 text-left font-medium">Last finished (IST)</th>
          </tr>
        </thead>
        <tbody>{students.map((s) => this.render_student_row(s, withCheckbox))}</tbody>
      </table>
    </div>
  );

  render_warnings = () => {
    const { warnings } = this.state;
    if (!warnings.not_in_pod && !warnings.not_approved) return null;
    const msgs = [];
    if (warnings.not_in_pod) msgs.push("the target isn't in these students' pod");
    if (warnings.not_approved) msgs.push('the target concierge is not APPROVED');
    return (
      <div className="mt-2 rounded border px-3 py-2 text-xs" style={WARN_STYLE}>
        Heads up: {msgs.join(' and ')}. The auto-assignment engine could later move these students
        again. You can still proceed.
      </div>
    );
  };

  render_eligible_modal = () => {
    const { show_eligible, eligible_loading, eligible_list } = this.state;
    if (!show_eligible) return null;
    return (
      <CustomModal
        show={show_eligible}
        close={this.close_eligible}
        showCloseBtn
        inner_style={MODAL_INNER}
      >
        <div className="p-4" style={{ minWidth: '320px' }}>
          <h3 className="text-base font-semibold">Eligible students</h3>
          {eligible_loading && <CustomLoading />}
          {!eligible_loading && eligible_list.length === 0 && (
            <div className="mt-3 text-xs" style={MUTED_STYLE}>
              No eligible students.
            </div>
          )}
          {!eligible_loading && eligible_list.length > 0 && (
            <>
              <div className="mt-1 text-xs" style={MUTED_STYLE}>
                {eligible_list.length} student{eligible_list.length === 1 ? '' : 's'}
              </div>
              {this.render_students_table(eligible_list, false)}
            </>
          )}
        </div>
      </CustomModal>
    );
  };

  render_review_modal = () => {
    const { show_review, review_students, committing, preview_available } = this.state;
    if (!show_review) return null;
    const selected = this.selected_uuids().length;
    return (
      <CustomModal
        show={show_review}
        close={this.close_review}
        showCloseBtn
        inner_style={MODAL_INNER}
      >
        <div className="p-4" style={{ minWidth: '320px' }}>
          <h3 className="text-base font-semibold">Review reassignment</h3>
          <div className="mt-1 text-xs" style={MUTED_STYLE}>
            {selected} of {review_students.length} selected · {preview_available} eligible in pool
          </div>
          {this.render_warnings()}
          {this.render_students_table(review_students, true)}
          <div className="mt-3 flex items-center justify-end gap-2">
            <Button
              onClick={this.close_review}
              custom_style={{ padding: '6px 14px', fontSize: '13px' }}
            >
              Cancel
            </Button>
            <Button
              onClick={this.do_commit}
              disabled={committing || selected === 0}
              custom_style={{ padding: '6px 14px', fontSize: '13px' }}
            >
              {committing ? 'Reassigning…' : `Confirm reassignment (${selected})`}
            </Button>
          </div>
        </div>
      </CustomModal>
    );
  };

  render() {
    const { access_type } = this.props;
    // Defense-in-depth: /concierge-pods is already SUPER_USER-only (so this route is gated by
    // the first-segment access check), but guard the page explicitly too.
    if ((access_type || '').toUpperCase() !== 'SUPER_USER') {
      return (
        <div style={PAGE_STYLE}>
          <CommonHeader title="Concierge — Reassignments" />
          <div className="mx-auto max-w-4xl px-3 py-6 text-sm" style={MUTED_STYLE}>
            Access restricted to super-users.
          </div>
        </div>
      );
    }
    const {
      advisors,
      source_uuid,
      region_id,
      user_type,
      from,
      to,
      pods,
      pods_loading,
      target_uuid,
      count,
      preview_loading,
      committing,
      result
    } = this.state;
    const eligibleCount = this.eligible_count();
    // The "from" date can't go further back than 30 days; both bounds stay within [30d ago, today].
    const today = moment().format('YYYY-MM-DD');
    const minFrom = moment().subtract(30, 'days').format('YYYY-MM-DD');
    return (
      <div style={PAGE_STYLE}>
        <CommonHeader
          title="Concierge — Reassignments"
          loading={pods_loading || preview_loading || committing}
        />
        <div className="mx-auto max-w-4xl px-3 py-4 sm:px-6">
          <Link to="/concierge-pods" className="text-sm" style={LINK_STYLE}>
            &larr; Back to Concierge Pods
          </Link>

          <div className="mt-3 rounded-lg border p-4" style={CARD_STYLE}>
            <div className="flex flex-col flex-wrap gap-3 sm:flex-row sm:items-end">
              <label className="text-xs" style={MUTED_STYLE}>
                Source concierge
                <select
                  name="source_uuid"
                  value={source_uuid}
                  onChange={this.on_source_change}
                  className="mt-1 block w-full rounded border px-2 py-1.5 text-sm sm:w-56"
                  style={INPUT_STYLE}
                >
                  <option value="">Select…</option>
                  {advisors.map((a) => (
                    <option key={a.uuid} value={a.uuid}>
                      {advisor_name(a)}
                    </option>
                  ))}
                </select>
              </label>

              {source_uuid && (
                <label className="text-xs" style={MUTED_STYLE}>
                  Pod
                  <select
                    name="region_id"
                    value={region_id}
                    onChange={this.on_region_change}
                    className="mt-1 block w-full rounded border px-2 py-1.5 text-sm sm:w-48"
                    style={INPUT_STYLE}
                  >
                    <option value="">All pods</option>
                    {pods.map((p) => (
                      <option key={p.region_id} value={p.region_id}>
                        {p.name} ({p.eligible_count})
                      </option>
                    ))}
                  </select>
                </label>
              )}

              {source_uuid && (
                <label className="text-xs" style={MUTED_STYLE}>
                  User type
                  <select
                    name="user_type"
                    value={user_type}
                    onChange={this.on_user_type_change}
                    className="mt-1 block w-full rounded border px-2 py-1.5 text-sm sm:w-52"
                    style={INPUT_STYLE}
                  >
                    {USER_TYPE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              <label className="text-xs" style={MUTED_STYLE}>
                Entered bucket — from
                <input
                  type="date"
                  name="from"
                  value={from}
                  min={minFrom}
                  max={to || today}
                  onChange={this.on_window_change}
                  className="mt-1 block w-full rounded border px-2 py-1.5 text-sm sm:w-40"
                  style={INPUT_STYLE}
                />
              </label>
              <label className="text-xs" style={MUTED_STYLE}>
                to
                <input
                  type="date"
                  name="to"
                  value={to}
                  min={from || minFrom}
                  max={today}
                  onChange={this.on_window_change}
                  className="mt-1 block w-full rounded border px-2 py-1.5 text-sm sm:w-40"
                  style={INPUT_STYLE}
                />
              </label>
            </div>

            {source_uuid && (
              <div className="mt-3 text-sm">
                <span className="font-semibold">{eligibleCount}</span>{' '}
                <span style={MUTED_STYLE}>
                  {user_type ? `${USER_TYPE_LABELS[user_type]} ` : ''}students, entered between{' '}
                  {from} and {to}
                </span>
                {eligibleCount > 0 && (
                  <button
                    type="button"
                    onClick={this.open_eligible}
                    className="ml-2 underline"
                    style={LINK_STYLE}
                  >
                    View list &raquo;
                  </button>
                )}
              </div>
            )}

            {source_uuid && (
              <div className="mt-4 flex flex-col flex-wrap gap-3 sm:flex-row sm:items-end">
                <label className="text-xs" style={MUTED_STYLE}>
                  Reassign to
                  <select
                    name="target_uuid"
                    value={target_uuid}
                    onChange={this.on_change}
                    className="mt-1 block w-full rounded border px-2 py-1.5 text-sm sm:w-56"
                    style={INPUT_STYLE}
                  >
                    <option value="">Select…</option>
                    {advisors
                      .filter((a) => a.uuid !== source_uuid)
                      .map((a) => (
                        <option key={a.uuid} value={a.uuid}>
                          {advisor_name(a)}
                        </option>
                      ))}
                  </select>
                </label>
                <label className="text-xs" style={MUTED_STYLE}>
                  How many
                  <input
                    type="number"
                    name="count"
                    min="1"
                    value={count}
                    onChange={this.on_change}
                    className="mt-1 block w-full rounded border px-2 py-1.5 text-sm sm:w-28"
                    style={INPUT_STYLE}
                  />
                </label>
                <Button
                  onClick={this.do_preview}
                  disabled={preview_loading}
                  custom_style={{ padding: '6px 14px', fontSize: '13px' }}
                >
                  {preview_loading ? 'Loading…' : 'Preview selection'}
                </Button>
              </div>
            )}

            {result && (
              <div
                className="mt-4 rounded border px-3 py-2 text-sm"
                style={
                  result.moved === 0 || result.skipped || result.not_found
                    ? WARN_STYLE
                    : {
                        background: 'var(--state-success-bg)',
                        color: 'var(--state-success-text)',
                        borderColor: 'var(--state-success)'
                      }
                }
              >
                Reassigned {result.moved} student{result.moved === 1 ? '' : 's'}
                {result.skipped ? `, skipped ${result.skipped} (no longer on source)` : ''}
                {result.not_found ? `, ${result.not_found} not found` : ''}.
              </div>
            )}
          </div>
        </div>

        {this.render_eligible_modal()}
        {this.render_review_modal()}
      </div>
    );
  }
}

ConciergePodsReassignments.propTypes = {
  set_notification_variable: PropTypes.func.isRequired,
  access_type: PropTypes.string
};

ConciergePodsReassignments.defaultProps = {
  access_type: ''
};

const mapStateToProps = (state) => ({
  access_type: state.home.access_type
});

const mapDispatchToProps = (dispatch) => ({
  set_notification_variable: (show, type, mes) =>
    dispatch(actions.set_notification_variable(show, type, mes))
});

export default connect(mapStateToProps, mapDispatchToProps)(ConciergePodsReassignments);
